import { FIELDS } from './catalog';
import { addDays } from './matching';
import type { BuildingAttrs, MockBuilding } from './types';

/** 60 fictitious buildings around place Bellecour (Lyon 2e), deterministic so a test session is reproducible. */

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const RANGES: Record<string, [number, number]> = {
  dwellings: [0, 180], year: [1850, 2022], area: [300, 12000], heatConsumption: [80, 1800], coldConsumption: [0, 600],
  fans: [0, 12], engieConsumption: [50, 1500], engiePdl: [0, 8], grdfConsumption: [40, 1400], grdfPdl: [0, 12],
  enedisConsumption: [20, 900], enedisPdl: [1, 60], dleHeat: [100, 2000], dleCold: [0, 800],
  storeys: [1, 18], renovationYear: [1985, 2025], selectedHeating: [60, 1400], selectedHotWater: [10, 400],
  heatedSurface: [250, 11000], chimneyCount: [0, 6], hotInstallYear: [1975, 2024], cooledSurface: [0, 6000],
  coldInstallYear: [1995, 2024], engieHeating: [40, 1100], engieHotWater: [10, 350], networkPdl: [0, 5],
  declHeating: [50, 1300], declHotWater: [10, 380], declHeatTotal: [80, 1600], declColdTotal: [0, 700],
  crmBuildings: [1, 40],
};

const TEXTS: Record<string, string[]> = {
  owner: ['Lyon Métropole Habitat', 'SCI Bellecour', 'Hospices Civils de Lyon', 'Ville de Lyon', 'Foncière Rhône', 'Région AURA'],
  syndic: ['Foncia', 'Citya', 'Nexity', 'Oralia', 'Régie Simonneau'],
  // Display names only (detail panel): the name filter was removed by the PO.
  name: ['Hôpital Saint-Joseph', 'Lycée Ampère', 'Résidence Bellecour', 'Hôtel des Postes', 'Clinique du Parc', 'Immeuble Victor-Hugo', 'Gymnase Sala', 'Hôpital Desgenettes', 'Groupe scolaire Lamartine', 'Bureaux Confluence'],
  heatingSystemType: ['Chaudière gaz', 'Sous-station', 'PAC air/eau', 'Chaudière fioul', 'Convecteurs'],
  hotManager: ['Dalkia', 'Engie Solutions', 'Idex', 'Coriance', 'Hervé Thermique'],
  coldManager: ['Engie Solutions', 'Dalkia Froid', 'Climespace', 'Equans'],
  installationName: ['CHAUFF-BELLECOUR-01', 'SST-PRESQUILE-12', 'CHAUF-AMPERE', 'FROID-CONFLUENCE-03', 'SST-HCL-07'],
  crmName: ['Raccordement Presqu’île', 'Renouvellement P2 HCL', 'Offre froid Confluence', 'Extension réseau Bellecour', 'Avenant Lycée Ampère'],
  crmBusiness: ['Hospices Civils de Lyon', 'Lyon Métropole Habitat', 'Région AURA', 'SCI Bellecour', 'Foncière Rhône'],
  crmOwner: ['Claire Martin', 'Julien Bernard', 'Sophie Laurent', 'Thomas Moreau'],
  crmEntity: ['ENGIE Solutions Centre-Est', 'ENGIE Réseaux Lyon', 'ENGIE Solutions Sud-Est'],
};

/** Probability a new attribute is not documented (null) — mirrors sparse real data. */
const MISSING = 0.15;

function generate(rand: () => number, index: number): BuildingAttrs {
  const pick = <T,>(xs: T[]) => xs[Math.floor(rand() * xs.length)];
  const between = ([lo, hi]: [number, number]) => Math.round(lo + rand() * (hi - lo));
  const attrs: BuildingAttrs = {};
  const hasOffer = rand() < 0.4;

  for (const f of Object.values(FIELDS)) {
    if (f.id.startsWith('crm') && f.id !== 'crmPresent' && !hasOffer) { attrs[f.id] = null; continue; }
    const missing = f.isNew && f.id !== 'crmPresent' && rand() < MISSING;
    switch (f.kind) {
      case 'range':
      case 'declared':
        attrs[f.id] = missing ? null : between(RANGES[f.id] ?? [0, 100]);
        break;
      case 'radio':
        attrs[f.id] = f.id === 'crmPresent' ? hasOffer : missing && f.id.endsWith('Contract') ? false : rand() < 0.4;
        break;
      case 'multiselect':
      case 'checkboxes':
        attrs[f.id] = missing ? null : f.multiValued ? [...new Set([pick(f.options!), pick(f.options!)])] : pick(f.options!);
        break;
      case 'dpe':
        attrs[f.id] = missing ? null : f.multiValued ? [...new Set([pick([...'ABCDEFG']), pick([...'CDEFG']), pick([...'DEFG'])])] : pick([...'ABCDEFG']);
        break;
      case 'text':
        attrs[f.id] = missing ? null : f.multiValued ? [pick(TEXTS[f.id]), pick(TEXTS[f.id])] : pick(TEXTS[f.id] ?? ['—']);
        break;
      case 'exact':
        attrs[f.id] = `OPP-${String(240100 + index * 7).padStart(6, '0')}`;
        break;
      case 'dateRange':
        attrs[f.id] = missing ? null : addDays(f.id.startsWith('crm') ? between([-500, 240]) : between([-60, 1100]));
        break;
      case 'distance':
        attrs[f.id] = between([0, 600]);
        break;
      case 'perimeter':
        attrs[f.id] = 'Lyon 2e — Rhône (69)';
        break;
      default:
        attrs[f.id] = null;
    }
  }
  attrs.usages = pick(['Habitat collectif', 'Bureaux', 'Clinique / Hôpital', 'Enseignement', 'Hôtel', 'Autres commerces', 'Groupes scolaires']);
  attrs.secondaryUsage = rand() < 0.5 ? pick(['Bureaux', 'Clinique / Hôpital', 'Restaurant/café', 'Autres commerces', 'Santé']) : null;
  return attrs;
}

export const BUILDINGS: MockBuilding[] = (() => {
  const rand = mulberry32(69002);
  return Array.from({ length: 60 }, (_, i) => {
    const col = i % 10;
    const row = Math.floor(i / 10);
    const attrs = generate(rand, i);
    return {
      id: `BFR69000${String(i + 1).padStart(4, '0')}`,
      name: TEXTS.name[i % TEXTS.name.length],
      heat: attrs.heatConsumption as number,
      coords: [4.8265 + col * 0.0011, 45.7535 + row * 0.001 + (row >= 3 ? 0.0008 : 0)],
      attrs,
    };
  });
})();

export const BUILDINGS_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: BUILDINGS.map((b, i) => {
    const [x, y] = b.coords;
    return {
      type: 'Feature' as const,
      id: i + 1,
      properties: { id: b.id, heat: b.heat },
      geometry: { type: 'Polygon' as const, coordinates: [[[x, y], [x + 0.0008, y], [x + 0.0008, y + 0.0006], [x, y + 0.0006], [x, y]]] },
    };
  }),
};
